import React, { useState } from "react";
import { FiDownload, FiArrowRight } from "react-icons/fi";
import { planFields, createEmptyInvestmentPlan, normalizeInvestmentPlan, getInvestmentPlanProgress, readInvestmentPlan, saveInvestmentPlan, formatInvestmentPlan } from "../../features/investmentPlan";
import "./investmentPlan.css";

const reading = {
  goal: ["financial-goals", "練習設定目標"], horizon: ["financial-goals", "分清資金期限"],
  availableMoney: ["cash-flow", "先整理收支餘裕"], riskLimits: ["risk-drawdown", "認識回撤與風險"],
  allocation: ["asset-allocation", "認識資產配置"], contributions: ["dca-mechanics", "比較投入方式"],
  reviewRules: ["rebalancing", "寫下檢查條件"], openQuestions: ["information-check", "練習查證資訊"],
};
function initialPlan() {
  try { return readInvestmentPlan(localStorage); }
  catch { return { plan: createEmptyInvestmentPlan(), storageError: true }; }
}

export default function InvestmentPlanWorksheet({ onOpenLesson }) {
  const [workspace, setWorkspace] = useState(initialPlan);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [downloadError, setDownloadError] = useState(false);
  const progress = getInvestmentPlanProgress(workspace.plan);
  const changeField = (id, value) => {
    const plan = normalizeInvestmentPlan({ ...workspace.plan, [id]: value });
    let saved = false;
    try { saved = saveInvestmentPlan(localStorage, plan); } catch { /* The editable draft remains available. */ }
    setWorkspace({ plan, storageError: !saved });
    setDownloadStatus("");
  };
  const download = () => {
    let url, anchor;
    try {
      const exportedAt = new Date().toISOString();
      const body = formatInvestmentPlan(workspace.plan, { exportedAt });
      url = URL.createObjectURL(new Blob([body], { type: "text/markdown;charset=utf-8" }));
      anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `investment-plan-${exportedAt.slice(0, 10)}.md`;
      document.body.appendChild(anchor);
      anchor.click();
      setDownloadStatus("已送出下載，請確認瀏覽器的下載項目。檔案包含目前草稿與未填項目。");
      setDownloadError(false);
    } catch {
      setDownloadStatus("目前無法產生下載檔，請先複製需要保留的文字。");
      setDownloadError(true);
    } finally {
      anchor?.remove();
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  return <section className="finance-plan" aria-labelledby="finance-plan-title">
    <div className="finance-section-heading"><div><span className="finance-eyebrow">YOUR WORDS, YOUR PLAN</span><h2 id="finance-plan-title">把學到的觀念，寫成自己的計畫</h2><p>不確定的地方可以先留白，再從旁邊的課程補上理解。這是一份學習草稿，填完不代表已評估適合度。</p></div></div>
    <div className="finance-plan-overview finance-panel"><div><span>已整理的項目</span><strong>{progress.filled}<small> / {progress.total}</small></strong><progress aria-label="投資計畫填寫進度" value={progress.filled} max={progress.total} /></div><div><p>只儲存在這個瀏覽器，不會上傳。清除網站資料或更換裝置可能失去內容；可下載文字檔另存備份。</p><button type="button" className="finance-primary" onClick={download}><FiDownload aria-hidden="true" />下載計畫 .md</button></div></div>
    {workspace.storageError && <p className="finance-plan-warning" role="alert">瀏覽器儲存無法讀取或寫入。目前畫面仍可編輯，離開前請下載或複製草稿；不要假設內容已保存。</p>}
    {downloadStatus && <p className={downloadError ? "finance-plan-warning" : "finance-plan-download-status"} role={downloadError ? "alert" : "status"}>{downloadStatus}</p>}
    <div className="finance-plan-fields">{planFields.map((field, index) => <article className="finance-panel finance-plan-field" key={field.id} aria-labelledby={`finance-plan-label-${field.id}`}>
      <div className="finance-plan-field-heading"><span>{String(index + 1).padStart(2, "0")}</span><label id={`finance-plan-label-${field.id}`} htmlFor={`finance-plan-input-${field.id}`}>{field.label}</label><small>{workspace.plan[field.id].trim() ? "已記下" : "待整理"}</small></div>
      <p id={`finance-plan-prompt-${field.id}`}>{field.prompt}</p>
      <textarea id={`finance-plan-input-${field.id}`} value={workspace.plan[field.id]} maxLength={field.maxLength} aria-describedby={`finance-plan-prompt-${field.id}`} placeholder="用自己的話寫下想法，也可以註記待查證的地方。" onChange={event => changeField(field.id, event.target.value)} />
      <div className="finance-plan-field-footer"><small>{workspace.plan[field.id].length} / {field.maxLength} 字</small>{onOpenLesson && <button type="button" className="finance-text-button" onClick={() => onOpenLesson(reading[field.id][0])}>{reading[field.id][1]}<FiArrowRight aria-hidden="true" /></button>}</div>
    </article>)}</div>
    <p className="finance-local-note">{workspace.storageError ? "草稿目前可能無法保存。" : "編輯時會保存到本機瀏覽器。"}這份表不會替你選商品、下單或計算適合的配置比例。</p>
  </section>;
}
